/**
 * InActivityHandler Component
 * 
 * Implements ISO 27001 and CBE security control requirements:
 * 
 * 1. Idle Session Timeout: 20 minutes maximum for user inactivity
 *    - Monitors user activity (mouse, keyboard, scroll, click)
 *    - Shows warning 2 minutes before logout
 *    - Automatically logs out after 20 minutes of inactivity
 * 
 * 2. Global Session Timeout: 2 hours maximum regardless of activity
 *    - Based on JWT token expiration
 *    - Enforces secure session termination
 * 
 * 3. Secure Session Termination:
 *    - Clears all authentication tokens
 *    - Clears localStorage and sessionStorage
 *    - Redirects to login page
 *    - Handles API logout for server-side session cleanup
 * 
 * Security Events Monitored:
 * - mousemove, keydown, scroll, click
 * - JWT token expiration
 * - User inactivity periods
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { clearAuthToken } from '../../utils/authUtils';
import { logoutApi } from 'src/services/userServicer';
import { goToDccLogin } from '../../utils/dccLogin';
import { isPublicPath } from '../../utils/authPaths';
import { clearAppStorage } from '../../utils/languageStorage';

const InactivityHandler = () => {
  const logoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The logout the warning schedules. Held in a ref because it must be cancellable: it is
  // armed inside the warning callback, and without a handle neither a later interaction
  // nor unmounting could stop it, so anyone who came back during the warning window was
  // signed out anyway, mid-work.
  const pendingLogoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // App mounts this component above the router outlet, so it runs on every route - the
  // login screen included, where it used to arm a logout for a session that does not
  // exist: a warning about an expiry nobody faced, then a logoutApi() call with no cookie.
  const onPublicPath = isPublicPath(location.pathname);

  // The ISO 27001 / CBE figures documented at the top of this file. Nothing else
  // hardcodes a duration, so these two are enough to change (handy for testing with
  // short values).
  const INACTIVITY_TIMEOUT = 20 * 60 * 1000;
  const WARNING_BEFORE_LOGOUT = 2 * 60 * 1000;
  // The warning should stay up until the logout it predicts actually happens.
  const WARNING_DISPLAY_TIME = WARNING_BEFORE_LOGOUT;

  const GLOBAL_SESSION_MS = 2 * 60 * 60 * 1000; // 2 hours - global session timeout

  // Lets the warning be dismissed once it no longer applies, and keeps a burst of
  // interactions from stacking duplicates.
  const WARNING_TOAST_ID = 'session-warning';

  // "2 minutes" / "30 seconds" - derived so the toast never promises a delay the timers
  // are no longer using.
  const WARNING_LABEL =
    WARNING_BEFORE_LOGOUT >= 60 * 1000
      ? `${Math.round(WARNING_BEFORE_LOGOUT / 60000)} minutes`
      : `${Math.round(WARNING_BEFORE_LOGOUT / 1000)} seconds`;

  useEffect(() => {
    if (onPublicPath) {
      // A warning can follow us here: goToDccLogin leaves for another origin, but an
      // in-app navigation to /login keeps <ToastContainer /> mounted, so the notice
      // outlives the page it belonged to.
      toast.dismiss(WARNING_TOAST_ID);
      return;
    }

    const handleLogout = async () => {
      // The redirect below leaves the toast container mounted, so a live warning would
      // still be on screen after the logout it predicted had happened.
      toast.dismiss(WARNING_TOAST_ID);
      try {
        const responseData = await logoutApi();
        if (responseData.success) {
          clearAuthToken();
          clearAppStorage();
          toast("user logged out successfully", { type: "success" });
          goToDccLogin('timeout');
        } else {
          clearAuthToken();
          clearAppStorage();
          toast("you logged out but session is still active connect with admin", { type: "error" });
          goToDccLogin('timeout');
        }
      } catch (error) {
        toast('An error occurred during logout. Please log in again.', { type: 'error' });
        clearAppStorage();
        clearAuthToken();
        goToDccLogin('timeout');
      }
    };

    // Global session timeout (2 hours from page load; auth via cookies so no token exp to read)
    const startTokenTimeout = () => {
      if (tokenTimeoutRef.current) clearTimeout(tokenTimeoutRef.current);
      tokenTimeoutRef.current = setTimeout(handleLogout, GLOBAL_SESSION_MS);
    };

    // Start the inactivity logout timeout (INACTIVITY_TIMEOUT - ISO 27001 compliant)
    const startLogoutTimeout = () => {
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
      // Interaction during the warning window cancels the logout it scheduled and takes
      // the warning down, so the session really is extended rather than just postponed.
      if (pendingLogoutRef.current) {
        clearTimeout(pendingLogoutRef.current);
        pendingLogoutRef.current = null;
        toast.dismiss(WARNING_TOAST_ID);
      }

      logoutTimeoutRef.current = setTimeout(() => {
        // Warn WARNING_BEFORE_LOGOUT ahead of the logout
        toast(`Session will expire in ${WARNING_LABEL} due to inactivity. Please save your work.`, {
          type: 'warning',
          autoClose: WARNING_DISPLAY_TIME,
          toastId: WARNING_TOAST_ID
        });

        // ...then log out, completing INACTIVITY_TIMEOUT since the last interaction
        pendingLogoutRef.current = setTimeout(handleLogout, WARNING_BEFORE_LOGOUT);
      }, INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT);
    };

    // Add event listeners to reset inactivity timeout
    const resetInactivityTimeout = () => {
      startLogoutTimeout();
    };

    const events = ['mousemove', 'keydown', 'scroll', 'click'];
    events.forEach(event => window.addEventListener(event, resetInactivityTimeout));

    // Initialize both timeouts
    startTokenTimeout();
    startLogoutTimeout();

    // Cleanup on component unmount
    return () => {
      if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);
      if (pendingLogoutRef.current) clearTimeout(pendingLogoutRef.current);
      if (tokenTimeoutRef.current) clearTimeout(tokenTimeoutRef.current);
      events.forEach(event => window.removeEventListener(event, resetInactivityTimeout));
    };
  }, [navigate, onPublicPath]);

  return null; // This component doesn't render anything
};

export default InactivityHandler;
